/**
 * Ortak UI bileşenleri — Mantine wrapper.
 */
import {
  Badge, Card, Group, Stack, Text, Title, type MantineColor,
} from "@mantine/core";
import { type ReactNode } from "react";
import { formatTRY } from "./format";

/**
 * KPI Card — dashboard kartı.
 */
interface KPICardProps {
  title: string;
  value: ReactNode;
  trend?: { value: number; label?: string };
  icon?: ReactNode;
  color?: MantineColor;
}

export function KPICard({ title, value, trend, icon, color = "blue" }: KPICardProps) {
  const trendColor =
    trend == null ? "gray" : trend.value > 0 ? "green" : trend.value < 0 ? "red" : "gray";
  const trendSign = trend == null ? "" : trend.value > 0 ? "↑" : trend.value < 0 ? "↓" : "";

  return (
    <Card padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed">{title}</Text>
        {icon}
      </Group>
      <Title order={2} c={color}>{value}</Title>
      {trend != null && (
        <Group gap="xs" mt="xs">
          <Badge color={trendColor} variant="light">
            {trendSign} {Math.abs(trend.value).toFixed(1)}%
          </Badge>
          {trend.label && <Text size="xs" c="dimmed">{trend.label}</Text>}
        </Group>
      )}
    </Card>
  );
}

/**
 * Money display — kuruş → TL formatted.
 *
 * Opsiyonel metin stilleri (`fw`, `size`, `c`) bir `Text` üzerinden
 * uygulanır — tutarı bağlam içinde vurgulamak için (ör. toplam = kalın).
 */
export function MoneyDisplay({
  minor, fw, size, c,
}: {
  minor: number | null | undefined;
  fw?: number;
  size?: string;
  c?: MantineColor;
}) {
  if (fw == null && size == null && c == null) {
    return <span>{formatTRY(minor)}</span>;
  }
  // exactOptionalPropertyTypes: undefined prop geçilmesin diye koşullu spread
  return (
    <Text
      component="span"
      {...(fw != null && { fw })}
      {...(size != null && { size })}
      {...(c != null && { c })}
    >
      {formatTRY(minor)}
    </Text>
  );
}

/**
 * Status Badge — renkli + emoji icon.
 */
// Tüm durum etiketleri Türkçe — admin, POS, KDS ekranlarında ham
// İngilizce kod (READY, CONFIRMED...) gösterilmez.
const STATUS_MAP: Record<string, { color: MantineColor; emoji: string; label: string }> = {
  // Order — mutfak/sipariş durumu
  DRAFT: { color: "gray", emoji: "📝", label: "Taslak" },
  OPEN: { color: "blue", emoji: "📥", label: "Açık" },
  CONFIRMED: { color: "cyan", emoji: "✅", label: "Onaylandı" },
  PREPARING: { color: "yellow", emoji: "🍳", label: "Hazırlanıyor" },
  READY: { color: "lime", emoji: "🟢", label: "Hazır" },
  SERVED: { color: "teal", emoji: "🍽️", label: "Servis Edildi" },
  // CLOSED = sipariş ödeme alındı (kasaya geçti). "Kapandı" daha önce
  // kullanıcılar tarafından "masa kapatıldı" ile karıştırıldı; bu yüzden
  // daha net olan "Ödendi" terimi tercih edildi.
  CLOSED: { color: "green", emoji: "✔️", label: "Ödendi" },
  CANCELLED: { color: "red", emoji: "❌", label: "İptal" },
  // Table — masa durumu
  FREE: { color: "green", emoji: "🟢", label: "Boş" },
  RESERVED: { color: "yellow", emoji: "🟡", label: "Rezerve" },
  OCCUPIED: { color: "red", emoji: "🔴", label: "Dolu" },
  CLEANING: { color: "gray", emoji: "⚪", label: "Temizleniyor" },
  OUT_OF_SERVICE: { color: "dark", emoji: "⛔", label: "Servis Dışı" },
  // Reservation — rezervasyon durumu
  PENDING: { color: "gray", emoji: "⏳", label: "Beklemede" },
  AWAITING_PAYMENT: { color: "yellow", emoji: "💳", label: "Kapora Bekleniyor" },
  SEATED: { color: "teal", emoji: "🪑", label: "Masada" },
  COMPLETED: { color: "green", emoji: "✔️", label: "Tamamlandı" },
  NO_SHOW: { color: "red", emoji: "👻", label: "Gelinmedi" },
  // Payment — ödeme durumu
  FAILED: { color: "red", emoji: "⚠️", label: "Başarısız" },
  REFUNDED: { color: "orange", emoji: "↩️", label: "İade Edildi" },
};

export function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_MAP[status] || { color: "gray", emoji: "•", label: status };
  return (
    // Badge varsayılan olarak etiketi ellipsis ile kırpar; durum metni her
    // zaman tam okunmalı (dar tablo hücrelerinde "D..." gibi kesilmesin).
    <Badge
      color={cfg.color}
      variant="light"
      size="lg"
      styles={{ root: { maxWidth: "unset" }, label: { overflow: "visible" } }}
    >
      {cfg.emoji} {cfg.label}
    </Badge>
  );
}

/**
 * EmptyState — boş liste için.
 */
interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  cta?: ReactNode;
}

export function EmptyState({ icon, title, description, cta }: EmptyStateProps) {
  return (
    <Stack align="center" gap="md" py="xl">
      {icon && <div style={{ fontSize: 64 }}>{icon}</div>}
      <Title order={3}>{title}</Title>
      {description && <Text c="dimmed" ta="center">{description}</Text>}
      {cta}
    </Stack>
  );
}

/**
 * Page header — başlık + opsiyonel actions.
 */
export function PageHeader({
  title, subtitle, actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    // Başlık + aksiyon. Geniş aksiyon grupları (ör. Stok Operasyonları'nda
    // 3 buton) dar ekrana sığmazsa alt satıra SARAR — başlık asla harf harf
    // kırılmaz. Tek butonlu sayfalarda buton başlığın sağında kalır.
    // Başlık bloğu esner (flex:1) ama min içerik genişliğinin altına
    // sıkışmaz (flexBasis: auto + minWidth tanımlı içerik genişliği).
    <Group justify="space-between" align="flex-start" mb="lg" gap="sm">
      <div style={{ flex: "1 1 auto", minWidth: "min(100%, 12rem)" }}>
        <Title order={1}>{title}</Title>
        {subtitle && <Text c="dimmed" size="sm">{subtitle}</Text>}
      </div>
      {actions && (
        <Group wrap="wrap" gap="xs" style={{ flexShrink: 0 }}>{actions}</Group>
      )}
    </Group>
  );
}
